import { Component, EventEmitter, inject, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { MessageSchedule, ScheduleService } from '../../../../services/campaign/schedule.service';
import { MessagesService } from '../../../../services/campaign/messages.service';
import { WizardAudienceService } from '../../../../pages/organization/campaigns/create-campaign/wizard-audience.service';
import { formatCount, formatCountdown, formatWhen, smsParts } from '../../../../../utils/sms';
import {
  formatScheduleWhen,
  scheduleMoment,
} from '../../../../pages/organization/campaigns/campaign-list/campaign-facts';

interface TimeRow {
  at: Date | null;
  label: string;
  countdown: string;
  past: boolean;
}

interface ScheduleGroup {
  schedule: MessageSchedule;
  subject: string;
  times: TimeRow[];
}

/** Step 4: every send, soonest first within each message, with its countdown. */
@Component({
  selector: 'app-schedule-preview',
  standalone: true,
  imports: [FormWrapperComponent, MatTooltipModule],
  templateUrl: './schedule-preview.component.html',
  styleUrl: '../campaign-preview.css',
})
export class SchedulePreviewComponent {
  @Output() openPopupEvent = new EventEmitter<object>();
  scheduleService = inject(ScheduleService);
  private messagesService = inject(MessagesService);
  private audience = inject(WizardAudienceService);

  readonly formatCount = formatCount;

  constructor(private router: Router, private route: ActivatedRoute) {}

  get groups(): ScheduleGroup[] {
    const now = new Date();
    return this.scheduleService.messageSchedule.map((schedule) => {
      const current = this.messagesService.savedMessages.find((message) => message.id === schedule.message?.id);
      const times = (schedule.messageDates ?? [])
        .map((row) => {
          const at = scheduleMoment(row.date, row.time, row.timezone);
          return {
            at,
            label: formatScheduleWhen(row.date, row.time, row.timezone),
            countdown: formatCountdown(at, now),
            past: !!at && at.getTime() <= now.getTime(),
          };
        })
        .sort((a, b) => (a.at?.getTime() ?? 0) - (b.at?.getTime() ?? 0));
      return {
        schedule,
        subject: current?.subject || schedule.message?.subject || 'Untitled message',
        times,
      };
    });
  }

  get totalSends(): number {
    return this.scheduleService.messageSchedule.reduce((sum, schedule) => sum + (schedule.messageDates?.length ?? 0), 0);
  }

  /** "Tue 13 Oct, 09:00, in 2 days" for the soonest send still to come. */
  get nextLabel(): string {
    const now = Date.now();
    const next = this.groups
      .flatMap((group) => group.times)
      .filter((time) => time.at && time.at.getTime() > now)
      .sort((a, b) => (a.at as Date).getTime() - (b.at as Date).getTime())[0];
    return next?.at ? `${formatWhen(next.at)}, ${next.countdown}` : '';
  }

  /** SMS for every send: parts × audience, summed over each message's sends. */
  get totalUnits(): number | null {
    if (this.audience.count === null) return null;
    return this.scheduleService.messageSchedule.reduce((sum, schedule) => {
      const current = this.messagesService.savedMessages.find((message) => message.id === schedule.message?.id);
      const parts = smsParts(current?.body ?? schedule.message?.body).parts;
      return sum + parts * (schedule.messageDates?.length ?? 0) * (this.audience.count ?? 0);
    }, 0);
  }

  updateQueryParams(id: number | string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: id },
      queryParamsHandling: 'merge', // merge with existing query params
    });
  }

  openPopup(schedule: MessageSchedule) {
    this.updateQueryParams(schedule.id);
    this.openPopupEvent.emit(schedule);
  }

  deleteSchedule(id: number | string) {
    const index = this.scheduleService.messageSchedule.findIndex((item) => item.id == id);
    if (index !== -1) {
      // Remove the item from the array
      this.scheduleService.messageSchedule.splice(index, 1);
    }
  }
}
